import clsx from 'clsx'
import React from 'react'
import { makeStyles } from 'tss-react/mui'

import {
    classToFComponent,
    FCClassContext,
    ModuleView,
    PageHead,
    PageSubtitle,
    PageTitle,
    ScrollablePageView
} from 'src/utils/views'
import { TheActingScope } from '../ta_scopes'

import ConfirmExportDataLimit from 'src/main/views/components/ConfirmExportDataLimit'
import CompanyCardView from './ta_company_card_view'
import MapCardView from './ta_map_card_view'
import OverlayView from './ta_overlay_view'
import ProfessionalCardView from './ta_professional_card_view'
import ProfessionalDetailView from './ta_professional_detail_view'
import ScoreView from './ta_score_view'

const MAP_KEY = 'e4cffd84'

const TILE_ROW = 88

const useStyles = makeStyles()({
    overlay: {
        flexShrink: 0,
        alignSelf: 'stretch',
        marginRight: 5,
        marginBottom: 5
    },
    dashboard: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr)) minmax(0, 4fr)',
        gridTemplateRows: `${TILE_ROW}px minmax(300px, 1fr) minmax(360px, 1.6fr)`,
        gridTemplateAreas: [
            '"sc-prof sc-co sc-revn sc-hco map"',
            '"companies companies companies companies map"',
            '"profissionals profissionals profissionals profissionals profissionals"'
        ].join(' '),
        columnGap: 11,
        rowGap: 10,
        flexGrow: 1,
        minHeight: 0,
        '@media (max-width: 1050px)': {
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gridTemplateRows: `${TILE_ROW}px 380px minmax(300px, 1fr) minmax(360px, 1fr)`,
            gridTemplateAreas: [
                '"sc-prof sc-co sc-revn sc-hco"',
                '"map map map map"',
                '"companies companies companies companies"',
                '"profissionals profissionals profissionals profissionals"'
            ].join(' ')
        },
        '@media (max-width: 720px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: `repeat(4, ${TILE_ROW}px) 320px minmax(300px, 1fr) minmax(360px, 1fr)`,
            gridTemplateAreas: [
                '"sc-prof"',
                '"sc-co"',
                '"sc-revn"',
                '"sc-hco"',
                '"map"',
                '"companies"',
                '"profissionals"'
            ].join(' ')
        }
    },
    maximized: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0
    },
    g_map: { gridArea: 'map', minHeight: 0 },
    g_profissionals: { gridArea: 'profissionals', display: 'flex', minHeight: 0 },
    g_companies: { gridArea: 'companies', display: 'flex', minHeight: 0 },
    g_sc_prof: { gridArea: 'sc-prof' },
    g_sc_co: { gridArea: 'sc-co' },
    g_sc_revn: { gridArea: 'sc-revn' },
    g_sc_hco: { gridArea: 'sc-hco' },
    excluded: { display: 'none' }
})

type TheActingViewProps = {
    className?: string
    scope: TheActingScope
}

class TheActingViewClass implements FCClassContext<TheActingViewProps> {
    // :: Fields
    scope!: TheActingScope
    classes!: ReturnType<typeof useStyles>['classes']

    // :: emissors

    readonly emitOnWndKeyDown = (e: KeyboardEvent) => {
        this.scope.keyboard.ctrlKey = e.ctrlKey
    }

    readonly emitOnWndKeyUp = (e: KeyboardEvent) => {
        this.scope.keyboard.ctrlKey = e.ctrlKey
    }

    // :: Methods

    onSyncState({ scope }: TheActingViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    onAttach() {
        document.addEventListener('keydown', this.emitOnWndKeyDown)
        document.addEventListener('keyup', this.emitOnWndKeyUp)
    }

    onDetach() {
        document.removeEventListener('keydown', this.emitOnWndKeyDown)
        document.removeEventListener('keyup', this.emitOnWndKeyUp)
    }

    render(props: TheActingViewProps) {
        return (
            <>
                {this.renderContent(props)}
                <ConfirmExportDataLimit scope={this.scope.exportDataLimitScope} />
                <ProfessionalDetailView scope={this.scope.professionalDetail} />
            </>
        )
    }

    renderPageHead() {
        return (
            <PageHead>
                <div>
                    <PageTitle>Profissionais Médicos</PageTitle>
                    <PageSubtitle>
                        Descubra onde os profissionais médicos atuam e a quais empresas e estabelecimentos de saúde
                        estão vinculados — busque na barra superior ou recorte pelo mapa.
                    </PageSubtitle>
                </div>
            </PageHead>
        )
    }

    renderContent(props: TheActingViewProps) {
        switch (this.scope.selectedCard) {
            case 'company':
                return this.renderCompany(props)
            case 'professional':
                return this.renderProfessional(props)
            case 'map':
                return this.renderMap(props)
            default:
                return this.renderDefault(props)
        }
    }

    renderCompany({ className, scope }: TheActingViewProps) {
        const classes = this.classes
        return (
            <ModuleView className={className}>
                <ScrollablePageView>
                    <div className={classes.maximized}>
                        <CompanyCardView scope={scope.companyCard} />
                    </div>
                    <div className={classes.excluded}>
                        <MapCardView key={MAP_KEY} scope={scope.mapCard} />
                    </div>
                </ScrollablePageView>
                <OverlayView className={classes.overlay} scope={scope.overlay} />
            </ModuleView>
        )
    }

    renderProfessional({ className, scope }: TheActingViewProps) {
        const classes = this.classes
        return (
            <ModuleView className={className}>
                <ScrollablePageView>
                    <div className={classes.maximized}>
                        <ProfessionalCardView scope={scope.professionalCard} />
                    </div>
                    <div className={classes.excluded}>
                        <MapCardView key={MAP_KEY} scope={scope.mapCard} />
                    </div>
                </ScrollablePageView>
                <OverlayView className={classes.overlay} scope={scope.overlay} />
            </ModuleView>
        )
    }

    renderMap({ className, scope }: TheActingViewProps) {
        const classes = this.classes
        return (
            <ModuleView className={className}>
                <ScrollablePageView>
                    <div className={classes.maximized}>
                        <MapCardView key={MAP_KEY} scope={scope.mapCard} />
                    </div>
                </ScrollablePageView>
                <OverlayView className={classes.overlay} scope={scope.overlay} />
            </ModuleView>
        )
    }

    renderDefault({ className, scope }: TheActingViewProps) {
        const classes = this.classes
        return (
            <ModuleView className={className}>
                <ScrollablePageView>
                    {this.renderPageHead()}
                    <div className={classes.dashboard}>
                        <ScoreView className={classes.g_sc_prof} scope={scope.profissionalScoreCard} />
                        <ScoreView className={classes.g_sc_co} scope={scope.companyScoreCard} />
                        <ScoreView className={classes.g_sc_revn} scope={scope.revenueScoreCard} />
                        <ScoreView className={classes.g_sc_hco} scope={scope.healthCoScoreCard} />
                        <MapCardView key={MAP_KEY} className={classes.g_map} scope={scope.mapCard} />
                        <div className={clsx(classes.g_companies)}>
                            <CompanyCardView scope={scope.companyCard} />
                        </div>
                        <div className={clsx(classes.g_profissionals)}>
                            <ProfessionalCardView scope={scope.professionalCard} />
                        </div>
                    </div>
                </ScrollablePageView>
                <OverlayView className={classes.overlay} scope={scope.overlay} />
            </ModuleView>
        )
    }
}

export const TheActingView = classToFComponent(TheActingViewClass, React)
export default TheActingView
